import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./AddContactModal.css";
import Calendar from "./Calendar";
import { useContacts } from "../context/ContactsContext";
import { useTags } from "../context/TagsContext";
import { searchTags } from "../api/tags";

export default function AddContactModal({ open, onClose, onSubmit }) {
  const cleanLeadingIcons = (value) =>
    (value || "").replace(/^[\uE000-\uF8FF]+/g, "").trimStart();
  const overlayRef = useRef(null);

  // === Context-Daten ===
  const {
    owners,           // z.B. [{id, name}] oder ["Anna", ...]
    contactMethods,   // z.B. [{id, name}] oder ["E-Mail", ...]
    contactType,      // z.B. [{id, name}] oder ["Lead", ...]
    addCustomerType,
    addContactMethod,
    createContact,
  } = useContacts();
  
  // Helper, um aus beliebigen Strukturen ein Label zu ziehen (name/label/title oder String)
  const toLabel = (x) =>
    typeof x === "string"
      ? x
      : x?.name ?? x?.label ?? x?.title ?? String(x ?? "");


  const toId = (x, idx, prefix) => {
  if (typeof x === "string") return `${prefix}-s-${idx}-${x}`;
    return String(
        x?.id ?? x?.key ?? x?.value ?? x?.code ?? x?.email ?? `${prefix}-${idx}-${toLabel(x)}`
    );
  };


  const normalizeOptions = (arr, prefix) =>
  (arr ?? []).map((x, i) => ({ id: toId(x, i, prefix), label: toLabel(x) }));

  const TYPE_OPTIONS   = normalizeOptions(contactType, "type");
  const METHOD_OPTIONS = normalizeOptions(contactMethods, "method");
  const OWNER_OPTIONS  = normalizeOptions(owners, "owner");

  // Tag search and selection
  const [newTag, setNewTag] = useState("");
  const [tagSearchResults, setTagSearchResults] = useState([]);
  const [isSearchingTags, setIsSearchingTags] = useState(false);

  // Neue Options hinzufügen
  const [isAddingCustomerType, setIsAddingCustomerType] = useState(false);
  const [newCustomerType, setNewCustomerType] = useState("");
  const [isAddingContactMethod, setIsAddingContactMethod] = useState(false);
  const [newContactMethod, setNewContactMethod] = useState("");

  // Links
  const [links, setLinks] = useState([]);
  const [newLinkName, setNewLinkName] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");

  // Formular
  const [formData, setFormData] = useState({
    name: "",
    firma: "",
    address: "",
    email: "",
    phone: "",
    tags: [],            // Tag names (will be sent to backend)
    customerTypes: [],   // Mehrfachauswahl (IDs)
    contactMethods: [],  // Mehrfachauswahl (IDs)
    contactOwner: "",    // Single-Select (ID)
    customerInfo: "",
    eventDetails: "",
    selectedDay: null,   // vom Calendar ausgewählter Tag (Date)
  });

  // Reset-Funktion für das Formular
  const resetForm = () => {
    setFormData({
      name: "",
      firma: "",
      address: "",
      email: "",
      phone: "",
      tags: [],
      customerTypes: [],
      contactMethods: [],
      contactOwner: "",
      customerInfo: "",
      eventDetails: "",
      selectedDay: null,
    });
    setLinks([]);
    setNewTag("");
    setTagSearchResults([]);
    setIsAddingCustomerType(false);
    setNewCustomerType("");
    setIsAddingContactMethod(false);
    setNewContactMethod("");
    setNewLinkName("");
    setNewLinkUrl("");
  };

  // Modal schließen mit Reset
  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  useEffect(() => {
    function onKeyDown(e) { if (e.key === "Escape") handleClose(); }
    if (open) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => overlayRef.current?.querySelector("#add-contact-name")?.focus(), 0);
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Multi-Select (Types/Methods)
  const toggleIntoArray = (field, value) => {
    setFormData((prev) => {
      const set = new Set(prev[field]);
      set.has(value) ? set.delete(value) : set.add(value);
      return { ...prev, [field]: Array.from(set) };
    });
  };

  // Single-Select (Owner)
  const setSingleOwner = (value) => {
    setFormData((prev) => ({
      ...prev,
      contactOwner: prev.contactOwner === value ? "" : value,
    }));
  };

  const handleAddTag = (tagName = null) => {
    const tagToAdd = tagName || newTag;
    if (!tagToAdd.trim()) return;
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tagToAdd.trim()]
    }));
    setNewTag("");
    setTagSearchResults([]);
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  // Debounced Tag-Suche
  useEffect(() => {
    if (!newTag.trim()) {
      setTagSearchResults([]);
      return;
    }

    setIsSearchingTags(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchTags(newTag);
        setTagSearchResults(results || []);
      } catch (err) {
        // Silent fail
        setTagSearchResults([]);
      } finally {
        setIsSearchingTags(false);
      }
    }, 300); // 300ms Debounce

    return () => clearTimeout(timer);
  }, [newTag]);

  const handleAddCustomerType = async () => {
    if (!newCustomerType.trim()) return;
    try {
      const result = await addCustomerType(newCustomerType);
      // Automatisch auswählen
      setFormData(prev => ({
        ...prev,
        customerTypes: [...prev.customerTypes, result.id]
      }));
      setNewCustomerType("");
      setIsAddingCustomerType(false);
    } catch (err) {
      // Silent fail
    }
  };

  const handleAddContactMethod = async () => {
    if (!newContactMethod.trim()) return;
    try {
      const result = await addContactMethod(newContactMethod);
      // Automatisch auswählen
      setFormData(prev => ({
        ...prev,
        contactMethods: [...prev.contactMethods, result.id]
      }));
      setNewContactMethod("");
      setIsAddingContactMethod(false);
    } catch (err) {
      // Silent fail
    }
  };

  const handleAddLink = () => {
    if (!newLinkName.trim() || !newLinkUrl.trim()) return;
    const newLink = {
      id: Date.now(), // Temp ID für Frontend
      name: newLinkName,
      url: newLinkUrl,
    };
    setLinks(prev => [...prev, newLink]);
    setNewLinkName("");
    setNewLinkUrl("");
  };

  const handleRemoveLink = (linkId) => {
    setLinks(prev => prev.filter(l => l.id !== linkId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Daten vorbereiten für Backend
    const contactData = {
      name: formData.name,
      company: formData.firma,
      address: cleanLeadingIcons(formData.address),
      email: formData.email,
      phone: cleanLeadingIcons(formData.phone),
      customerInfo: formData.customerInfo,
      customerTypeIds: formData.customerTypes,
      contactMethodIds: formData.contactMethods,
      ownerIds: formData.contactOwner ? [formData.contactOwner] : [],
      tags: formData.tags,
    };

    // Event hinzufügen falls vorhanden
    if (formData.selectedDay && formData.eventDetails) {
      const formatLocalDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      };
      
      contactData.event = {
        day: formatLocalDate(formData.selectedDay),
        title: formData.name || "Event",
        description: formData.eventDetails,
      };
    }

    try {
      // Links zur Kontaktdaten hinzufügen
      if (links.length > 0) {
        contactData.links = links;
      }

      await createContact(contactData);
      onSubmit?.(formData);
      handleClose();
    } catch (err) {
      // Silent fail
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="add-contact-modal-overlay"
      id="add-contact-modal-overlay"
      ref={overlayRef}
      onClick={(e) => {        
        if (e.target === e.currentTarget) handleClose();   
      }}
    >
      <div
        className="add-contact-modal"
        id="add-contact-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* leerer Header (kein Titel/X) */}
        <div className="add-contact-header-empty" />

        <form className="add-contact-body" id="add-contact-form" onSubmit={handleSubmit}>
          <div className="add-contact-info-container" id="add-contact-info-container">
            {/* Name + Firma */}
            <div className="add-contact-contact-header">
              <div className="add-contact-contact-name">
                <label htmlFor="add-contact-name">Name</label>
                <input
                  id="add-contact-name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange("name")}
                />
              </div>

              <div className="add-contact-contact-company">
                <label htmlFor="add-contact-firma">Firma</label>
                <input
                  id="add-contact-firma"
                  type="text"
                  value={formData.firma}
                  onChange={handleChange("firma")}
                />
              </div>
            </div>

            <div className="add-contact-contact-company">
              <label htmlFor="add-contact-address">Adresse</label>
              <input
                id="add-contact-address"
                type="text"
                value={formData.address}
                onChange={handleChange("address")}
              />
            </div>

            {/* Tags: Suche immer offen */}
            <div className="add-contact-contact-tags">
              {formData.tags.map((tag, idx) => (
                <span key={idx} className="tag tag-removable">
                  {tag}
                  <button
                    type="button"
                    className="tag-remove"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    ✕
                  </button>
                </span>
              ))}
              <div className="add-contact-add-tag-input-wrapper">
                <input
                  className="add-contact-tag-input"
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                  placeholder="Tag suchen oder erstellen…"
                />
                <button
                  type="button"
                  className="add-contact-add-tag-confirm"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleAddTag}
                  aria-label="Tag hinzufügen"
                >
                  +
                </button>
                {newTag.trim() && (isSearchingTags || tagSearchResults.length > 0) && (
                  <div className="add-contact-tag-dropdown">
                    {isSearchingTags ? (
                      <div className="add-contact-tag-dropdown-item" style={{ color: "#666", fontStyle: "italic" }}>
                        Suche läuft...
                      </div>
                    ) : tagSearchResults.length > 0 ? (
                      tagSearchResults.map((tag) => (
                        <div
                          key={tag.tagId || tag.id}
                          className="add-contact-tag-dropdown-item"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            handleAddTag(tag.name || tag.label);
                          }}
                        >
                          {tag.name || tag.label}
                        </div>
                      ))
                    ) : null}
                  </div>
                )}
                {newTag.trim() && !isSearchingTags && tagSearchResults.length === 0 && (
                  <div className="add-contact-tag-dropdown">
                    <div className="add-contact-tag-dropdown-item" style={{ color: "#666", fontStyle: "italic" }}>
                      Keine Tags gefunden. Enter drücken zum Erstellen.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="add-contact-contact-body">
              <div className="add-contact-contact-left">
                <label htmlFor="add-contact-email">Email</label>
                <input
                  id="add-contact-email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                />

                <label htmlFor="add-contact-phone">Telefon</label>
                <input
                  id="add-contact-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange("phone")}
                />

                {/* Customer Types – Multi */}
                <label>Customer Type</label>
                <div className="add-contact-pills-scroll">
                <div className="add-contact-pills-row">
                    {TYPE_OPTIONS.map((opt) => {
                    const active = formData.customerTypes.includes(opt.id);
                    return (
                        <button
                        key={opt.id}
                        type="button"
                        className={`add-contact-pill ${active ? "active" : ""}`}
                        aria-pressed={active}
                        onClick={() => toggleIntoArray("customerTypes", opt.id)}
                        >
                        {opt.label}
                        </button>
                    );
                    })}
                    {!isAddingCustomerType ? (
                      <button
                        type="button"
                        className="add-contact-pill add-pill-btn"
                        onClick={() => setIsAddingCustomerType(true)}
                      >
                        +
                      </button>
                    ) : (
                      <div className="add-contact-inline-input">
                        <input
                          type="text"
                          value={newCustomerType}
                          onChange={(e) => setNewCustomerType(e.target.value)}
                          onBlur={() => setIsAddingCustomerType(false)}
                          onKeyDown={(e) => e.key === "Enter" && handleAddCustomerType()}
                          placeholder="Neuer Type..."
                          autoFocus
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleAddCustomerType}
                        >
                          +
                        </button>
                      </div>
                    )}
                </div>
                </div>

                {/* Contact Methods – Multi */}
                <label>Contact Method</label>
                <div className="add-contact-pills-scroll">
                <div className="add-contact-pills-row">
                    {METHOD_OPTIONS.map((opt) => {
                    const active = formData.contactMethods.includes(opt.id);
                    return (
                        <button
                        key={opt.id}
                        type="button"
                        className={`add-contact-pill ${active ? "active" : ""}`}
                        aria-pressed={active}
                        onClick={() => toggleIntoArray("contactMethods", opt.id)}
                        >
                        {opt.label}
                        </button>
                    );
                    })}
                    {!isAddingContactMethod ? (
                      <button
                        type="button"
                        className="add-contact-pill add-pill-btn"
                        onClick={() => setIsAddingContactMethod(true)}
                      >
                        +
                      </button>
                    ) : (
                      <div className="add-contact-inline-input">
                        <input
                          type="text"
                          value={newContactMethod}
                          onChange={(e) => setNewContactMethod(e.target.value)}
                          onBlur={() => setIsAddingContactMethod(false)}
                          onKeyDown={(e) => e.key === "Enter" && handleAddContactMethod()}
                          placeholder="Neue Method..."
                          autoFocus
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleAddContactMethod}
                        >
                          +
                        </button>
                      </div>
                    )}
                </div>
                </div>

                {/* Contact Owner – Single */}
                <label>Contact Owner</label>
                <div className="add-contact-pills-scroll">
                <div className="add-contact-pills-row">
                    {OWNER_OPTIONS.map((opt) => {
                    const active = formData.contactOwner === opt.id;
                    return (
                        <button
                        key={opt.id}
                        type="button"
                        className={`add-contact-pill ${active ? "active single" : "single"}`}
                        aria-pressed={active}
                        onClick={() => setSingleOwner(opt.id)}
                        >
                        {opt.label}
                        </button>
                    );
                    })}
                </div>
                </div>

              </div>

              <div className="add-contact-contact-right">
                <label>Kalender</label>

                {/* Wichtig: dieser Wrapper sorgt dafür, dass der Kalender den verfügbaren Platz einnimmt */}
                <div className="add-contact-calendar-flex">
                  <div className="add-contact-calendar-scroll">
                    <Calendar
                        selectedDate={formData.selectedDay}              // NEU
                        onSelect={(date) => setFormData((p) => ({ ...p, selectedDay: date }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Links: Eingabe immer offen */}
            <div className="add-contact-contact-links">
              {links.map((link) => (
                <span key={link.id} className="tag tag-removable">
                  {link.name}
                  <button
                    type="button"
                    className="tag-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveLink(link.id);
                    }}
                    title="Link entfernen"
                  >
                    ✕
                  </button>
                </span>
              ))}
              <div className="add-contact-add-link-wrapper">
                <input
                  type="text"
                  value={newLinkName}
                  onChange={(e) => setNewLinkName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddLink()}
                  placeholder="Link Name..."
                />
                <input
                  type="url"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddLink()}
                  placeholder="https://..."
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleAddLink}
                >
                  +
                </button>
              </div>
            </div>

            {/* Unten: Details */}
            <div className="add-contact-info-details-wrapper">
              <div className="add-contact-customer-info">
                <label htmlFor="add-contact-customerInfo">Customer Info</label>
                <textarea
                  id="add-contact-customerInfo"
                  value={formData.customerInfo}
                  onChange={handleChange("customerInfo")}
                />
              </div>

              <div className="add-contact-event-details">
                <label htmlFor="add-contact-eventDetails">Event Details</label>
                <textarea
                  id="add-contact-eventDetails"
                  value={formData.eventDetails}
                  onChange={handleChange("eventDetails")}
                />
              </div>
            </div>
          </div>

          {/* Footer: Abbrechen links / Erstellen rechts */}
          <div className="add-contact-footer">
            <div className="add-contact-footer-left">
              <button type="button" className="add-contact-btn secondary" id="add-contact-cancel" onClick={handleClose}>
                Abbrechen
              </button>
            </div>
            <div className="add-contact-footer-right">
              <button type="submit" className="add-contact-btn primary" id="add-contact-create">
                Erstellen
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
